'use client';

import BarLoader from '@/components/ui/loaders/BarLoader';
import useAuthFunc from '@/hooks/auth/useAuthFunc';
import { getKycSession, kycSuccess } from '@/services/kyc/kyc.service';
import { TSession } from '@/services/kyc/kyc.types';
import { Box, Text } from '@chakra-ui/react';
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { signupStyles } from '../signup-templates/signup.styles';
import { BiSolidMessageAltError } from 'react-icons/bi';
import { API_CONFIG } from '@/config';

const Kyc = () => {
   const iframeRef = useRef<HTMLIFrameElement>(null);
   const [session, setSession] = useState<TSession | null>(null);
   const { redirect, refreshSession, updateSession } = useAuthFunc();
   const [isLoading, setIsLoading] = useState(true);

   const createSession = useCallback(async () => {
      setIsLoading(true);
      const [error, data] = await getKycSession();
      setIsLoading(false);
      if (error) {
         console.log(error);
         return;
      }

      setSession(data.response);
   }, []);

   useEffect(() => {
      createSession();
   }, [createSession]);

   useEffect(() => {
      const origin = 'https://api.yoti.com';

      const handleMessage = async (event: MessageEvent) => {
         const eventType = event.data.eventType;
         if (event.origin === origin && eventType === 'SUCCESS' && session) {
            const [error, data] = await kycSuccess(session.sessionId);

            if (error) {
               console.log(error);
               return;
            }

            await refreshSession();
            redirect(data?.response?.registration_step);
            window.removeEventListener('message', handleMessage);
         }
         if (event.origin === origin && eventType === 'STARTED' && session) {
            iframeRef.current?.contentWindow?.postMessage(
               {
                  eventType: 'INIT_SESSION',
                  sessionID: session.sessionId,
                  sessionToken: session.clientSessionToken,
               },
               origin,
            );
         }
      };

      if (session) window.addEventListener('message', handleMessage);

      return () => {
         window.removeEventListener('message', handleMessage);
      };
   }, [session, redirect, refreshSession, updateSession]);

   if (isLoading) return <BarLoader loadingText="Creating KYC session" />;

   if (!session)
      return (
         <Box css={signupStyles.kyc_area} h="100vh" alignItems="center">
            <Box display="grid" placeItems="center" bg="gray.900" padding="30px" borderRadius={8} gap="4">
               <BiSolidMessageAltError color="red" fill="red" size={40} />
               <Text textAlign="center" color="red" fontWeight="bold">
                  Failed to create session
               </Text>
            </Box>
         </Box>
      );

   return (
      <Box css={signupStyles.kyc_area}>
         <Box css={signupStyles.kyc_con}>
            <iframe
               ref={iframeRef}
               src={`${API_CONFIG.YOTI_ID}/web/index.html?sessionID=${session.sessionId}&sessionToken=${session.clientSessionToken}`}
               style={{ height: '100vh', width: '100%', border: 'none' }}
               allow="camera"
            ></iframe>
         </Box>
      </Box>
   );
};

export default Kyc;
